import { compile } from '@casa/layout';
import type { StatusState, TileOf, TileStyle } from '@casa/model';
import type { IconDefinition } from '../../elements/icon.ts';
import { iconElement, textElement, type LayoutElement } from '../../elements/layout_elements.ts';
import { DEFAULT_VALUE_COLOR, formatTileNumber, type TileFont } from '../value/value_tile.ts';

/**
 * The `status` tile (docs/specs/status-tile.md, issue #17): its source's value as a state, the
 * first of its states that matches it, shown as an icon above a text in the state's colour; with a
 * command, a tap opens a card to choose another state, which is sent (a mode).
 */

export type StatusTile = TileOf<'status'>;

/** A number from a value: itself, or a text that is one; else null. */
function numberOf(value: unknown) : number | null {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const number = Number(value);
        return Number.isFinite(number) ? number : null;
    }

    return null;
}

/**
 * Whether `value` is the state's value (ST-2): the same number where both are numbers, else the same
 * text without regard to case or spaces around it (`true` is `"true"`).
 */
export function isSameValue(stateValue: string | number | boolean, value: unknown) : boolean {
    if (value === null || value === undefined) {
        return false;
    }
    const [a, b] = [numberOf(stateValue), numberOf(value)];
    if (a !== null && b !== null) {
        return a === b;
    }

    return String(stateValue).trim().toLowerCase() === String(value).trim().toLowerCase();
}

/** Whether the state is the one for `value`: its value, else its condition on a number, else every value. */
export function stateMatches(state: StatusState, value: unknown) : boolean {
    if (value === null || value === undefined) {
        return false;
    }
    if (state.value !== undefined) {
        return isSameValue(state.value, value);
    }
    if (state.when !== undefined) {
        const number = numberOf(value);
        if (number === null) {
            return false;
        }
        try {
            const result = compile(state.when).evaluate({ value: number });
            return typeof result === 'number' && result !== 0 && !Number.isNaN(result);
        } catch {
            return false;
        }
    }

    return true;
}

/** The state shown for `value` (ST-3): the first that matches, top to bottom. */
export function stateOf(states: readonly StatusState[], value: unknown) : StatusState | undefined {
    return states.find((state) => stateMatches(state, value));
}

/**
 * What a status tile shows of its source's value. A device read whole, as its role's record
 * (device-roles DR-2), stands for its main state: a light for on or off (`switch`), a thermostat for
 * its mode, else what it is doing. Another record, or a list, has no state a status tile can show:
 * nothing (`-`), not "[object Object]" (the Mac's check of the Hue driver, 2026-11-03).
 */
export function stateValueOf(value: unknown) : unknown {
    if (typeof value !== 'object' || value === null) {
        return value;
    }
    if (Array.isArray(value)) {
        return undefined;
    }

    const record = value as Record<string, unknown>;
    if ('switch' in record) {
        return record.switch;
    }
    if ('setpoint' in record) {
        return record.mode ?? record.state;
    }

    return undefined;
}

/** A value as a text, where no state names it: a number in the tile's format. */
export function valueText(value: unknown, numberFormat: string, locale: string | undefined) : string {
    if (value === null || value === undefined) {
        return '-';
    }
    if (typeof value === 'number') {
        return formatTileNumber(value, numberFormat, locale);
    }

    return String(value);
}

export interface StatusTileContent {
    text: string;
    /** The icon's name; undefined for none. */
    icon: string | undefined;
    color: string;
    /** The state's tile style (QS-4), over the tile's own. */
    style: TileStyle | undefined;
    show: 'both' | 'icon' | 'text';
}

export function statusTileContent(tile: StatusTile, read: unknown, locale: string | undefined, textColor = DEFAULT_VALUE_COLOR) : StatusTileContent {
    const { states, numberFormat, show } = tile.options;
    const value = stateValueOf(read);
    const state = stateOf(states, value);
    const plain = valueText(value, numberFormat, locale);

    return {
        text: state?.text ?? plain,
        icon: state?.icon,
        color: state?.color ?? textColor,
        style: state?.style,
        show,
    };
}

export interface ModeChoice {
    /** What the source reads in this state, shown at once when it is chosen. */
    value: string | number | boolean;
    /** What the command sends for it. */
    send: string | number | boolean;
    text: string;
    icon: string | undefined;
    color: string | undefined;
}

/** The states a mode tile offers (ST-5): those with a value, in their order. */
export function modeChoices(tile: StatusTile) : ModeChoice[] {
    return tile.options.states.flatMap((state) =>
        state.value === undefined ? [] : [{ value: state.value, send: state.send ?? state.value, text: state.text ?? String(state.value), icon: state.icon, color: state.color }],
    );
}

/**
 * The tile's look (ST-4): the icon above the text, both in the state's colour; the text alone, as
 * large as fits, where there is no icon or `show` says so; the icon alone where `show` says so.
 */
export function statusTileElements(content: StatusTileContent, icon: IconDefinition | undefined, font: TileFont) : LayoutElement[] {
    const textStyle = { family: font.family, metrics: font.metrics };
    const showsIcon = content.show !== 'text' && content.icon !== undefined && icon !== undefined;
    const showsText = content.show !== 'icon' || !showsIcon;

    if (showsIcon && !showsText) {
        return [iconElement({ center: 'parent.center', w: 'parent.width*0.8', h: 'parent.height*0.8' }, icon, content.color, { fill: true })];
    }
    if (!showsIcon) {
        return [textElement({ center: 'parent.center', w: 'parent.width*0.9', h: 'parent.height*0.42' }, content.text, content.color, textStyle)];
    }

    return [
        iconElement({ ref: 'icon', centerX: 'parent.center.X', top: 'parent.top+parent.height*0.08', w: 'min(parent.width*0.8,parent.height*0.52)', h: 'min(parent.width*0.8,parent.height*0.52)' }, icon, content.color, { fill: true }),
        textElement({ centerX: 'parent.center.X', top: 'icon.bottom+parent.height*0.08', w: 'parent.width*0.9', h: 'parent.height*0.24' }, content.text, content.color, textStyle),
    ];
}
